import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';

import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import pdf1 from './assets/blog1.pdf';
import pdf2 from './assets/blog2.pdf';
import blogImage from './assets/blogimage.png';
import { tArray } from '../lib/i18nSafe';

const Blog = () => {
  const { t } = useTranslation();
  const posts = tArray(t, 'blog.posts');
  const shouldReduceMotion = useReducedMotion();

  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [docsError, setDocsError] = useState('');

  const [adminToken, setAdminToken] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  const fadeUp = useMemo(
    () => ({
      initial: { opacity: 0, y: shouldReduceMotion ? 0 : 16 },
      whileInView: { opacity: 1, y: 0 },
      viewport: { once: true, amount: 0.25 },
      transition: { duration: 0.6, ease: 'easeOut' }
    }),
    [shouldReduceMotion]
  );

  const loadDocuments = useCallback(async () => {
    setLoadingDocs(true);
    setDocsError('');
    try {
      const res = await fetch('/api/blog/list');
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setDocuments(Array.isArray(data?.documents) ? data.documents : []);
    } catch (e) {
      setDocsError(t('blog.documentsLoadError'));
    } finally {
      setLoadingDocs(false);
    }
  }, [t]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  const onUpload = async () => {
    setUploadError('');
    setUploadSuccess('');
    if (!file) return setUploadError(t('blog.uploadNoFile'));

    setUploading(true);
    try {
      const res = await fetch(`/api/blog/upload?filename=${encodeURIComponent(file.name)}`, {
        method: 'POST',
        headers: {
          'x-upload-token': adminToken,
          'content-type': file.type || 'application/octet-stream'
        },
        body: file
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Upload failed');
      }

      setFile(null);
      setUploadSuccess(t('blog.uploadSuccess'));
      await loadDocuments();
    } catch (e) {
      setUploadError(t('blog.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} md={7}>
          <Box component={motion.div} {...fadeUp}>
            <Typography variant="h4" fontWeight={900} sx={{ mb: 1 }}>
              {t('blog.title')}
            </Typography>
            <Typography sx={{ opacity: 0.85 }}>{t('blog.subtitle')}</Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            component="img"
            src={blogImage}
            alt={t('blog.imageAlt')}
            loading="lazy"
            decoding="async"
            sx={{
              width: '100%',
              maxWidth: 420,
              display: 'block',
              borderRadius: 4,
              boxShadow: '0 18px 40px rgba(0,0,0,0.08)',
              ml: { md: 'auto' },
              mx: { xs: 'auto', md: 0 },
              aspectRatio: { xs: '16 / 10', md: '16 / 9' },
              objectFit: 'cover'
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mt: 1 }}>
        {posts.map((post) => (
          <Grid key={post.id} item xs={12} sm={6} md={4}>
            <Box component={motion.div} {...fadeUp}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 4,
                  height: '100%',
                  transition: 'transform 220ms ease, box-shadow 220ms ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 18px 40px rgba(0,0,0,0.10)'
                  }
                }}
              >
                <CardContent>
                  <Typography variant="h6" fontWeight={900} sx={{ mb: 0.5 }}>
                    {post.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {post.excerpt}
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  {(() => {
                    const url =
                      post?.url ||
                      (post.id === 1 ? pdf1 : null) ||
                      (post.id === 2 ? pdf2 : null);

                    if (!url) return null;

                    return (
                      <Button variant="contained" color="primary" href={url} target="_blank" rel="noopener noreferrer">
                        {t('common.downloadPdf')}
                      </Button>
                    );
                  })()}
                </CardActions>
              </Card>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Box component={motion.div} {...fadeUp}>
          <Typography variant="h5" fontWeight={900} sx={{ mb: 0.75 }}>
            {t('blog.documentsTitle')}
          </Typography>
          <Typography sx={{ opacity: 0.85, mb: 2.5 }}>{t('blog.documentsSubtitle')}</Typography>
        </Box>

        <Paper variant="outlined" sx={{ borderRadius: 4, p: { xs: 2, md: 2.5 }, mb: 2 }}>
          <Stack spacing={1.25}>
            <Typography fontWeight={900}>{t('blog.uploadTitle')}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {t('blog.uploadHint')}
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }}>
              <TextField
                size="small"
                label={t('blog.uploadTokenLabel')}
                value={adminToken}
                onChange={(e) => setAdminToken(e.target.value)}
                sx={{ flex: 1 }}
              />

              <Button variant="outlined" component="label" sx={{ whiteSpace: 'nowrap' }}>
                {file ? t('blog.uploadChangeFile') : t('blog.uploadChooseFile')}
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    const next = e.target.files?.[0] || null;
                    setFile(next);
                    setUploadError('');
                    setUploadSuccess('');
                  }}
                />
              </Button>

              <Button variant="contained" disabled={!file || uploading} onClick={onUpload} sx={{ whiteSpace: 'nowrap' }}>
                {uploading ? t('blog.uploadUploading') : t('blog.uploadButton')}
              </Button>
            </Stack>

            {uploading ? <LinearProgress /> : null}
            {uploadError ? <Alert severity="error">{uploadError}</Alert> : null}
            {uploadSuccess ? <Alert severity="success">{uploadSuccess}</Alert> : null}
            {file ? (
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {t('blog.uploadSelected')}: {file.name}
              </Typography>
            ) : null}
          </Stack>
        </Paper>

        {loadingDocs ? <LinearProgress /> : null}
        {docsError ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {docsError}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          {documents.map((d) => {
            const name = String(d.pathname || '').split('/').pop() || t('blog.documentDefaultName');
            return (
              <Grid key={d.url} item xs={12} md={6}>
                <Box component={motion.div} {...fadeUp}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 4,
                      p: 2,
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'transform 200ms ease, box-shadow 200ms ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 18px 40px rgba(0,0,0,0.08)'
                      }
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={900} noWrap title={name}>
                        {name}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.75 }}>
                        {d.uploadedAt ? new Date(d.uploadedAt).toLocaleString() : ''}
                      </Typography>
                    </Box>

                    <Button variant="contained" href={d.url} target="_blank" rel="noopener noreferrer">
                      {t('common.downloadPdf')}
                    </Button>
                  </Paper>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Container>
  );
};

export default Blog;